import { useEffect } from 'react';
import { toast } from 'sonner';
import { decodeCartFromUrl, stripCartParam } from '@/utils/cartShare';
import { cartUtils } from '@/data/translatedData';
import { useLanguage } from '@/context/LanguageContext';
import { tx } from '@/translations/translations';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const IMPORT_FLAG = '_mitzva_shared_cart_imported';

/**
 * Detects ?cart=... in the URL on first mount.
 * Fetches the live products from the API, merges the shared items
 * into the local cart, and offers a confirmation toast with action.
 *
 * Renders nothing.
 */
const SharedCartImporter = ({ onCartChanged }) => {
  const { language } = useLanguage();

  useEffect(() => {
    // Use a window-level flag to dedupe across React 18 StrictMode double-mount
    if (window[IMPORT_FLAG]) return;
    const sharedItems = decodeCartFromUrl();
    if (!sharedItems || sharedItems.length === 0) return;
    window[IMPORT_FLAG] = true;

    const importCart = async () => {
      try {
        const res = await fetch(`${API_URL}/api/products`);
        if (!res.ok) throw new Error('failed');
        const products = await res.json();

        const productById = new Map(products.map(p => [p.product_id, p]));

        let added = 0, skipped = 0;
        for (const s of sharedItems) {
          const dbProduct = productById.get(s.id);
          if (!dbProduct) { skipped += 1; continue; }
          const color = (dbProduct.colors || []).find(c => c.value === s.selectedColor?.value);
          const product = {
            id: dbProduct.product_id,
            name: dbProduct.name?.[language] || dbProduct.name?.es || '',
            price: dbProduct.price_mxn,
            image: color?.image || dbProduct.image,
            material: dbProduct.material,
            style: dbProduct.style,
            isPromo: dbProduct.is_promo,
            noTzitzit: dbProduct.no_tzitzit,
            hasCottonType: dbProduct.has_cotton_type,
            selectedTradition: s.selectedTradition,
            selectedHilos: s.selectedHilos,
            selectedCottonType: s.selectedCottonType,
            selectedColor: color
              ? { name: color.name?.[language] || color.name?.es || '', value: color.value, image: color.image }
              : (s.selectedColor || undefined),
          };
          cartUtils.addToCart(product, s.size, s.quantity || 1);
          added += 1;
        }

        window.dispatchEvent(new Event('cartUpdated'));
        if (typeof onCartChanged === 'function') onCartChanged();
        stripCartParam();

        if (added > 0) {
          toast.success(
            tx(language,
              `Carrito compartido importado (${added} producto${added === 1 ? '' : 's'})`,
              `Shared cart imported (${added} item${added === 1 ? '' : 's'})`,
              `סל משותף יובא (${added} מוצרים)`),
            { duration: 4000 }
          );
        }
        if (skipped > 0) {
          toast.error(
            tx(language,
              `${skipped} producto${skipped === 1 ? '' : 's'} no disponible${skipped === 1 ? '' : 's'}`,
              `${skipped} item${skipped === 1 ? '' : 's'} unavailable`,
              `${skipped} מוצרים לא זמינים`)
          );
        }
      } catch {
        toast.error(
          tx(language, 'No se pudo importar el carrito compartido', 'Could not import shared cart', 'לא ניתן לייבא את הסל המשותף')
        );
        stripCartParam();
      }
    };

    importCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default SharedCartImporter;
